/// <amd-module name="Feed/Legacy/Components/CreateBadgePanel" />
import * as React from "react";

import { Dropdown, DropdownMenuItemType, IDropdownOption } from "OfficeFabric/Dropdown";
import { Label } from "OfficeFabric/Label";
import { Panel, PanelType } from "OfficeFabric/Panel";

import * as Utils_String from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { Uri } from "VSS/Platform/Util/Url";

import { Link } from "VSSUI/Link";

import { CiConstants } from "Feed/Common/Constants/Constants";
import { FeedView, Package } from "Feed/Common/Contracts/Feed";
import * as PackageResources from "Feed/Common/Resources";
import * as Actions from "Feed/Legacy/Actions/Actions";
import { PackageMessagePanel } from "Feed/Legacy/Components/PackageMessagePanel";
import * as UrlHelper from "Feed/Legacy/Helpers/UrlHelper";
import { CopyableTextField, ICopyableTextFieldTelemetry } from "Feed/Legacy/Protocols/Components/CopyableTextField";
import { Feed } from "Feed/Legacy/WebApi/VSS.CustomFeed.Contracts";
import { Badge } from "Utils/Badge";

export interface ICreateBadgePanelProps {
    pageContext: IVssPageContext;
    /**
     * the currently selected feed
     */
    feed: Feed;

    /**
     * the views available on the feed
     */
    views?: FeedView[];

    /**
     * the currently selected package
     */
    selectedPackage: Package;
}

export interface ICreateBadgePanelState {
    /**
     * true if there was an error loading the preview
     * (probably because package isn't in the selected view, or only pre-release versions are in the feed)
     */
    previewError: boolean;

    /**
     * message to show if there was an error loading the preview
     */
    warningMessage: string;

    /**
     * the view that is selected
     */
    selectedViewId: string;
    selectedViewName: string;
}

export class CreateBadgePanel extends React.Component<ICreateBadgePanelProps, ICreateBadgePanelState> {
    private static readonly noViewKey = "_noView";
    private static readonly dividerKey = "_divider";

    constructor(props: ICreateBadgePanelProps) {
        super(props);

        this.state = {
            previewError: false,
            warningMessage: null,
            selectedViewId: null,
            selectedViewName: null
        };
    }

    public render(): JSX.Element {
        const viewOptions = this._viewsDropDownOptions(this.props.views);

        return (
            <Panel
                className={"create-badge-panel"}
                isOpen={true}
                isLightDismiss={true}
                onDismiss={this._onDismiss}
                type={PanelType.medium}
                headerText={PackageResources.CreateBadgePanel_Header}
                closeButtonAriaLabel={PackageResources.AriaLabel_MultiCommandPanel_CloseButton}
            >
                {
                    <div>
                        {this.state.previewError && (
                            <div className={"create-badge-panel-block"}>
                                <PackageMessagePanel message={this.state.warningMessage} />
                            </div>
                        )}
                        <div className={"fontSizeM create-badge-panel-block"}>
                            {PackageResources.CreateBadgePanel_Description}
                        </div>
                        <div className={"create-badge-panel-block"}>
                            <Dropdown
                                label={PackageResources.CreateBadgePanel_ViewDropdown_Label}
                                options={viewOptions}
                                defaultSelectedKey={CreateBadgePanel.noViewKey}
                                onChanged={this._onViewChanged}
                            />
                        </div>
                        {this.state.previewError === false ? (
                            <div className={"create-badge-panel-block"}>
                                <Label>{PackageResources.CreateBadgePanel_Preview_Label}</Label>
                                <Link href={this._getPackageDetailsUrl()}>
                                    <img
                                        className={"preview-image"}
                                        alt={Utils_String.format(
                                            PackageResources.CreateBadgePanel_BadgeImage_AltText_Preview,
                                            this.props.selectedPackage.protocolType,
                                            this.props.selectedPackage.versions[0].version
                                        )}
                                        src={this._getBadgeUri().absoluteUri}
                                        onError={this._onImageError}
                                    />
                                </Link>
                            </div>
                        ) : null}
                        {this.state.previewError === false ? (
                            <div className={"create-badge-panel-block"}>
                                <Label>{PackageResources.CreateBadgePanel_Markdown_Label}</Label>
                                <CopyableTextField
                                    pageContext={this.props.pageContext}
                                    text={this._getMarkdownText()}
                                    buttonAriaLabel={
                                        PackageResources.AriaLabel_CopyToClipBoard_CopyMarkdown_ButtonLabel
                                    }
                                    textFieldAriaLabel={
                                        PackageResources.AriaLabel_CopyToClipBoard_CopyMarkdown_TextFieldLabel
                                    }
                                    telemetryProperties={
                                        {
                                            commandName: CiConstants.CopyCommandBadgeMarkdown,
                                            feedName: this.props.feed.name,
                                            packageName: this.props.selectedPackage.name,
                                            protocol: this.props.selectedPackage.protocolType
                                        } as ICopyableTextFieldTelemetry
                                    }
                                />
                            </div>
                        ) : null}
                        {this.state.previewError === false ? (
                            <div className={"margin-bottom"}>
                                <Label>{PackageResources.CreateBadgePanel_ImageLink_Label}</Label>
                                <CopyableTextField
                                    pageContext={this.props.pageContext}
                                    text={this._getBadgeUri().absoluteUri}
                                    buttonAriaLabel={
                                        PackageResources.AriaLabel_CopyToClipBoard_CopyImageLink_ButtonLabel
                                    }
                                    textFieldAriaLabel={
                                        PackageResources.AriaLabel_CopyToClipBoard_CopyImageLink_TextFieldLabel
                                    }
                                    telemetryProperties={
                                        {
                                            commandName: CiConstants.CopyCommandBadgeImage,
                                            feedName: this.props.feed.name,
                                            packageName: this.props.selectedPackage.name,
                                            protocol: this.props.selectedPackage.protocolType
                                        } as ICopyableTextFieldTelemetry
                                    }
                                />
                            </div>
                        ) : null}
                    </div>
                }
            </Panel>
        );
    }

    private get FeedName(): string {
        let feedName = this.props.feed.name;
        if (this.state.selectedViewName != null) {
            feedName = Utils_String.format("{0}@{1}", feedName, this.state.selectedViewName);
        }
        return feedName;
    }

    private get FeedId(): string {
        let feedId = this.props.feed.id;
        if (this.state.selectedViewId != null) {
            feedId = Utils_String.format("{0}@{1}", feedId, this.state.selectedViewId);
        }
        return feedId;
    }

    private _getMarkdownText(): string {
        const altText = Utils_String.format(
            PackageResources.CreateBadgePanel_BadgeImage_AltText,
            this.props.selectedPackage.name,
            this.FeedName,
            PackageResources.AzureArtifacts
        );
        return Utils_String.format(
            "[![{0}]({1})]({2})",
            altText,
            this._getBadgeUri().absoluteUri,
            this._getPackageDetailsUrl()
        );
    }

    private _getBadgeUri(): Uri {
        // Making call in component because this component is used in both feed & package controller views/stores
        // Move it out once we have a single store.
        const projectId = this.props.feed.project ? this.props.feed.project.id : null;
        return Badge.getPackageBadgeRestUri(
            this.props.pageContext,
            this.FeedId,
            this.props.selectedPackage.id,
            projectId
        );
    }

    private _getPackageDetailsUrl(): string {
        return UrlHelper.getLatestPackageDetailsPageUrl(
            this.props.pageContext,
            this.props.feed.id,
            this.props.selectedPackage,
            this.state.selectedViewId,
            "true" /* preferRelease */
        );
    }

    private _getNoBadgeText(): string {
        if (this.state.selectedViewName != null) {
            return Utils_String.format(
                PackageResources.CreateBadgePanel_BadgeLoadError_View,
                this.props.selectedPackage.name,
                this.state.selectedViewName
            );
        }
        return Utils_String.format(PackageResources.CreateBadgePanel_BadgeLoadError, this.props.selectedPackage.name);
    }

    private _viewsDropDownOptions(views: FeedView[]): IDropdownOption[] {
        const viewOptions = views.map(view => {
            return {
                key: view.name,
                text: Utils_String.format("@{0}", view.name),
                data: view.id
            } as IDropdownOption;
        });

        viewOptions.push({
            key: CreateBadgePanel.dividerKey,
            text: "-",
            itemType: DropdownMenuItemType.Divider
        } as IDropdownOption);

        viewOptions.push({
            key: CreateBadgePanel.noViewKey,
            text: PackageResources.CreateBadgePanel_NoView,
            data: null
        } as IDropdownOption);

        return viewOptions;
    }

    private _onImageError = (): void => {
        const warningMessage = this._getNoBadgeText();
        this.setState({
            previewError: true,
            warningMessage
        });
    };

    private _onViewChanged = (option: IDropdownOption): void => {
        const viewId = option.data as string;
        const viewName = option.key === CreateBadgePanel.noViewKey ? null : (option.key as string);
        if (viewId !== this.state.selectedViewId) {
            this.setState({
                previewError: false,
                warningMessage: null,
                selectedViewId: viewId,
                selectedViewName: viewName
            });
        }
    };

    private _onDismiss = (): void => {
        Actions.ToggleCreateBadgePanel.invoke(false);
    };
}
